// Pinia store that remembers where the user came from.
//
// Populated by a `router.afterEach` hook (see `@/router`). The Settings
// breadcrumb reads it to provide a contextual return path from anywhere in
// the app.
//
// We record the *previous* route (the `from` arg of afterEach) rather than
// browser history because we want a typed RouteLocation we can resolve,
// label, and link with `fullPath` (preserving query + hash). Browser
// `history.back()` doesn't carry route metadata.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

export interface PreviousRoute {
  name: string
  fullPath: string
  params: Record<string, string>
}

export const useNavigationStore = defineStore('navigation', () => {
  const previousRoute = ref<PreviousRoute | null>(null)

  function record(from: RouteLocationNormalized): void {
    // Initial cold load fires afterEach with a synthetic route whose
    // `name` is null — there's no "previous" page in that case.
    if (from.name == null) return

    // Don't record a public route (login, accept-invite, etc.). After OAuth
    // we land on /projects with `from = login`; "Back to Login" is the
    // wrong affordance.
    if (from.meta?.public === true) return

    // Don't record settings as its own back-target. The user could open
    // the user menu from settings and click Settings again, which would
    // otherwise produce "Back to Settings".
    if (from.name === 'settings') return

    previousRoute.value = {
      name: String(from.name),
      fullPath: from.fullPath,
      params: { ...(from.params as Record<string, string>) },
    }
  }

  function $reset(): void {
    previousRoute.value = null
  }

  return { previousRoute, record, $reset }
})
